import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { name: "Story", href: "#story", type: "anchor" },
  { name: "Menu", href: "/menu", type: "route" },
  { name: "Locations", href: "#locations", type: "anchor" },
  { name: "Contact Us", href: "#contact", type: "anchor" },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderNavLink = (link) => {
    if (link.type === "route") {
      return (
        <Link
          key={link.name}
          to={link.href}
          data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
          className={`text-sm font-medium tracking-[0.15em] uppercase transition-colors duration-300 hover:text-[#C9A85A] ${
            scrolled ? "text-[#191646]" : "text-white"
          }`}
        >
          {link.name}
        </Link>
      );
    }
    return (
      <a
        key={link.name}
        href={isHomePage ? link.href : `/${link.href}`}
        data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, "-")}`}
        className={`text-sm font-medium tracking-[0.15em] uppercase transition-colors duration-300 hover:text-[#C9A85A] ${
          scrolled ? "text-[#191646]" : "text-white"
        }`}
        onClick={() => {
          if (link.type === "anchor" && !isHomePage) {
            window.location.href = `/${link.href}`;
          }
        }}
      >
        {link.name}
      </a>
    );
  };

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-nav shadow-sm border-b border-[#C9A85A]/20" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo on the left */}
            <Link to="/" className="flex flex-col items-start" data-testid="logo-link">
              <span
                className={`font-heading text-2xl md:text-3xl font-semibold tracking-[0.08em] transition-colors duration-300 ${
                  scrolled ? "text-[#191646]" : "text-white"
                }`}
              >
                KIPLINGS
              </span>
              <span className="text-[10px] tracking-[0.3em] uppercase font-body font-medium text-[#C9A85A]">
                D&eacute;li & Bistro
              </span>
            </Link>

            {/* All nav links on the right */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map(renderNavLink)}
            </div>

            {/* Mobile hamburger */}
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className={scrolled ? "text-[#191646]" : "text-white"} size={24} />
              ) : (
                <Menu className={scrolled ? "text-[#191646]" : "text-white"} size={24} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile menu */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-4/5 max-w-[320px] bg-[#191646] z-50 transform transition-transform duration-300 lg:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <span className="font-heading text-2xl text-[#C9A85A] tracking-wider">KIPLINGS</span>
              <p className="text-[10px] text-white/50 tracking-[0.3em] uppercase">D&eacute;li & Bistro</p>
            </div>
            <button onClick={() => setMobileMenuOpen(false)}>
              <X className="text-white" size={24} />
            </button>
          </div>
          <div className="space-y-6">
            {navLinks.map((link) =>
              link.type === "route" ? (
                <Link
                  key={link.name}
                  to={link.href}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="block text-white/80 hover:text-[#C9A85A] text-lg font-heading tracking-wider transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={isHomePage ? link.href : `/${link.href}`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="block text-white/80 hover:text-[#C9A85A] text-lg font-heading tracking-wider transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};
